import { usePermission } from '../../../../auth/AuthContext';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import type { ApiError } from '../../../../lib/apiError';
import { anyNamePart } from '../routingForm';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import type { PersonNameParts } from '../routingTypes';

/**
 * The individual at an outside office — the sender of a received document, the addressee of a
 * dispatched one.
 *
 * **THE NAME IS WRITTEN TO `rsys_pii` AND NEVER READ BACK HERE** — the Supplier contact's shape.
 * The record carries a `PersonId`, so an edit genuinely cannot show the name already recorded;
 * resolving it here would be a second reveal from a form. The sentence above the fields says what
 * a blank means, because a reader who assumed it meant "unchanged" would detach the person by
 * correcting a subject line.
 *
 * **The name row is `sm:grid-cols-3`** — the one exception to the two-column form grid, because
 * it is one fact across three controls. BOTH outer columns are `<FieldGroup>`s — a salutation
 * leading the first name, a suffix trailing the last — and each segment keeps the id the server
 * names it by. The salutation is a TITLE rather than part of the name, which is the one place
 * this product groups something that is not strictly one value; it is recorded in
 * `.claude/rules/frontend-forms.md`.
 */
export function CorrespondentNameFields({
  idPrefix,
  role,
  field,
  value,
  onChange,
  editing,
  error,
  disabled,
  required = false,
  intro,
}: {
  idPrefix: string;
  /** Who this person is to the document — "sender" or "addressee". */
  role: string;
  /** The request's field stem — `sender`, `recipient`, `requestor` — which the four ids and errors derive from. */
  field: string;
  value: PersonNameParts;
  onChange: (next: PersonNameParts) => void;
  editing: boolean;
  error: ApiError | null;
  disabled: boolean;
  /**
   * The first and last name are REQUIRED (a press conference's external requestor). Off by default —
   * the routing correspondents keep their optional person exactly as before.
   */
  required?: boolean;
  /** Replaces the sentence above the fields, for a caller whose blank means something else. */
  intro?: string;
}) {
  // Decides the WORDING, not the fields — supplying a name is not a reveal.
  const canReadPeople = usePermission('personnel.read');
  const any = anyNamePart(value);

  return (
    <FormFieldset legend={`Named ${role}`}>
      <p className="-mt-1 mb-1 text-xs text-slate-500">
        {intro ?? (editing
          ? canReadPeople
            ? `The name lives in the identity store, so this form cannot show the one already recorded — the profile does. Leaving these blank CLEARS the ${role}; filling them replaces it.`
            : `The name lives in the identity store and you do not have the grant to be shown it. Leaving these blank CLEARS the ${role}; filling them replaces it.`
          : `Optional — the PERSON who signed or is named on it, where there is one. The office above is the ${role}'s organization. The name is written to the identity store and never kept on the document.`)}
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <FieldGroup
          label="First name"
          labelFor={`${idPrefix}-${field}-first-name`}
          description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
          segments={[
            {
              id: `${idPrefix}-${field}-salutation`,
              label: 'Salutation',
              value: value.salutation,
              onChange: (next) => onChange({ ...value, salutation: next }),
              options: SALUTATION_OPTIONS,
              allowCustom: true,
              error: error?.fieldError(`${field}Salutation`),
              disabled,
              // The addon gives way, never the grid — a name row is three equal columns.
              width: SALUTATION_SEGMENT_WIDTH,
              emptyLabel: SALUTATION_EMPTY_LABEL,
            },
            {
              id: `${idPrefix}-${field}-first-name`,
              label: 'First name',
              value: value.firstName,
              onChange: (next) => onChange({ ...value, firstName: next }),
              error:
                any && value.firstName.trim().length === 0
                  ? (required ? 'Give the first name.' : `Give the first name, or clear the whole ${role}.`)
                  : error?.fieldError(`${field}FirstName`),
              disabled,
              required,
            },
          ]}
        />

        <TextField
          id={`${idPrefix}-${field}-middle-name`}
          label="Middle name"
          value={value.middleName}
          onChange={(e) => onChange({ ...value, middleName: e.target.value })}
          maxLength={100}
          error={error?.fieldError(`${field}MiddleName`)}
          disabled={disabled}
        />

        <FieldGroup
          label="Last name"
          description="The narrow box is the suffix — Jr., Sr., III. It is part of the name, not a title."
          segments={[
            {
              id: `${idPrefix}-${field}-last-name`,
              label: 'Last name',
              value: value.lastName,
              onChange: (next) => onChange({ ...value, lastName: next }),
              error:
                any && value.lastName.trim().length === 0
                  ? (required ? 'Give the last name.' : `Give the last name, or clear the whole ${role}.`)
                  : error?.fieldError(`${field}LastName`),
              disabled,
              required,
            },
            {
              id: `${idPrefix}-${field}-suffix`,
              label: 'Suffix',
              value: value.suffix,
              onChange: (next) => onChange({ ...value, suffix: next }),
              error: error?.fieldError(`${field}Suffix`),
              disabled,
              width: 'w-20 shrink-0',
            },
          ]}
        />
      </div>
    </FormFieldset>
  );
}
